/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import TQTaskScheduler from "../controller/TQTaskScheduler";
import { TQUtils } from "../utils/TQUtils";
import TQInitParams from "./TQInitParam";

export namespace tqtypes {
    /** Status of TypeComp background processing */
    export enum StatusCode {
        UNINITIALIZED = "UNINITIALIZED",
        WORKING = "WORKING",
        DONE = "DONE",
        PAUSED = "PAUSED",
        ERROR_OCCURED = "ERROR_OCCURED",
    }

    export enum RuntimeMode {
        ALL = "ALL", // This means browse + found fonts
        FOUND_FONTS = "FOUND_FONTS", // found fonts only, no browse needed
    }

    // sort order to be specified in aggregation enumeration
    export enum SortOrderPreference {
        DEFAULT = 0,
        LANGUAGE = 1,
        ENTITLEMENT = 2,
        RECENTLY_UPDATED = 3,
    }

    export enum FontBufferType {
        RAW_PROTECTED = 0, // Default
        WOFF = 1,
        WOFF2 = 2,
        RAW = 4,
    }

    // How web fonts usage should reflect for selections / activations
    export enum InstallState {
        OS = "os", // For installing the reported fonts across system including third party usage
        CC = "cc", // For installing the reported fonts only for Adobe apps
    }

    /**
     * Access level for the font usage. This can be READ_ONLY or READ_WRITE
     */
    export enum AccessLevel {
        READ_WRITE = "READ_WRITE",
        READ_ONLY = "READ_ONLY",
    }

    /**
     * A type defining what TQWeb would return once font is found, TQFont object
     * and tqtypes.Error if any.
     */
    export type FindFontPromise = {
        error: tqtypes.Error;
        font: TQFont;
    };

    /** Error codes */
    export enum ErrorCode {
        // No error was encountered / observed, good to go.
        NONE = 0,

        // Either the user is offline, can not connect to internet OR connection lost in middle of in-progress load
        OFFLINE,

        // Either we could not reach typekit server OR
        // request made to typekit was bad request (Http 400) OR
        // there was internal server error(Http 500) OR
        // request got timed out
        SERVER_UNREACHABLE,

        // TypeComp received 401 (Not authorized) from typekit servers. If fatal error (In case Error flag isFatal is true).
        AUTH_FAILURE,

        PROXY_FAILURE,

        // JSON parsing error. For fonts Catalog JSON parsing error, Error flag isFatal is true
        JSON_PARSE_ERROR,

        BAD_REQUEST,

        // Http status 403 was received for the given operation. In case of a font if received, font is not licensed for the user
        FORBIDDEN,

        // Font was not found on typekit server
        FONT_NOT_FOUND,

        // Server doesn't want to handle requests and is currently overloaded
        TOO_MANY_REQUESTS,

        // TypeQuestWeb could not add the font as the cache capacity is full. This implies that the particular font is too big to fit in cache
        DISK_CACHE_CAPACITY_REACHED,

        //Either TypeQuestWeb was unable to create any directory within its cache or while inserting file into discache, TypeQuestWeb faced an issue
        DISK_WRITE_FAILED,

        // Font received is an invalid font
        BAD_FONT_DATA,

        // The error has occurred as user is reuploading an already uploaded BYOF font
        DUPLICATE_BYOF,

        // The configuration specified is not correct, you have missed setting up TQInitParams.
        INVALID_CONFIGURATION,

        // Generic Error
        UNKNOWN,
    }

    export enum DiskCacheType {
        NONE = 0, // Default
        OPFS = 1,
        IN_MEMORY = 2,
    }

    export enum AppEntitlement {
        PAID = 0, // Default
        FREEMIUM = 1, // For in-app upsell experience
    }

    /** Font language: Total 35 languages TypeQuest recognizes as described here
     https://github.com/typekit/speakeasy/blob/master/speakeasy.gemspec
     standard at https://www.iana.org/assignments/language-subtag-registry/language-subtag-registry
     */

    export enum Language {
        Default = "en" /** This contains "en" and  languages other than below mentioned languages. */,
        Arabic = "ar" /** ar - Arabic */,
        Armenian = "hy" /** hy - Armenian */,
        Belarusian = "be" /** be - Belarusian */,
        Bengali = "bn" /** bn - Bengali */,
        Catalan = "ca" /** ca - Catalan */,
        Cherokee = "chr" /** chr - Cherokee */,
        SimplifiedChinese = "zh-Hans" /** zh-Hans - SimplifiedChinese */,
        TraditionalChinese = "zh-Hant" /** zh-Hant - TraditionalChinese */,
        Mandarin = "zh-Latn-pinyin" /** zh-Latn-pinyin - Mandarin Chinese */,
        Croatian = "hr" /** hr - Croatian */,
        Czech = "cs" /** cs - Czech */,
        Danish = "da" /** da - Danish */,
        Devanagari = "Deva" /** Deva - Devanagari */,
        Dutch = "nl" /** nl - Dutch */,
        Emoji = "emoji" /** emoji - Emoji */,
        Finnish = "fi" /** fi - Finnish */,
        French = "fr" /** fr - French */,
        Filipino = "fil" /** fil - Filipino */,
        German = "de" /** de - German */,
        Greek = "el" /** el - Modern Greek */,
        Gujarati = "gu" /** gu - Gujarati */,
        Gurmukhi = "Guru" /** Guru - Gurmukhi */,
        Hindi = "hi" /** hi - Hindi */,
        Hebrew = "he" /** he - Hebrew */,
        Hungarian = "hu" /** hu - Hungarian */,
        Indonesian = "id" /** id- Indonesian */,
        IPA = "IPA" /** IPA International Phonetic Alphabet */,
        Italian = "it" /** it - Italian */,
        Japanese = "ja" /** ja - Japanese */,
        Kazakh = "kk" /** kk - Kazakh */,
        Kannada = "kn" /** kn - Kannada */,
        Khmer = "km" /** km - Khmer */,
        Korean = "ko" /** ko - Korean */,
        Latvian = "lv" /** lv - Latvian */,
        Lao = "lo" /** lo - Lao */,
        Macedonian = "mk" /** mk - Macedonian */,
        Malay = "ms" /** ms - Malaysia */,
        Malayalam = "ml" /** ml - Malayalam */,
        Maltese = "mt" /** mt - Maltese */,
        Myanmar = "my" /** my - Myanmar/Burmese */,
        Norwegian = "no" /** no - Norwegian */,
        Persian = "fa" /** fa - Persian */,
        Polish = "pl" /** pl - Polish */,
        Portuguese = "pt" /** pt - Portuguese */,
        Romanian = "ro" /** ro - Romanian */,
        Russian = "ru" /** ru - Russian */,
        Serbian = "sr" /** sr - Serbian */,
        Sinhalese = "si" /** si - Sinhalese */,
        Slovak = "sk" /** sk - Slovak */,
        Slovenian = "sl" /** sl - Slovenian */,
        Spanish = "es" /** es - Spanish */,
        Swedish = "sv" /** sv - Swedish */,
        Tamil = "ta" /** ta - Tamil */,
        Telugu = "te" /** te - Telugu  */,
        Thai = "th" /** th - Thai  */,
        Turkish = "tr" /** tr - Turkish */,
        Ukrainian = "uk" /** uk - Ukrainian */,
        Vietnamese = "vi" /** vi - Vietnamese */,
        HongKongCh = "HK" /** HK - HongKongCh */,
    }

    export enum Locale {
        Default = "en_US" /** This contains "en" and  languages other than below mentioned languages. */,
        Japanese = "ja_JP" /** ja - Japanese */,
        Gurmukhi = "pa_Guru" /** Guru - Gurumukhi */,
        HongKongCh = "zh_HK" /** HK - HongKongCh */,
        Arabic = "ar_AE" /** ar - Arabic */,
        Bengali = "bn_IN" /** bn - Bengali */,
        Catalan = "ca_ES" /** ca - Catalan */,
        Cherokee = "chr_US" /** chr - Cherokee */,
        Czech = "cs_CZ" /** cs - Czech */,
        Danish = "da_DK" /** da - Danish */,
        German = "de_DE" /** de - German */,
        Greek = "el_GR" /** el - Modern Greek */,
        Spanish = "es_ES" /** es - Spanish */,
        Emoji = "emoji" /** emoji - Emoji */,
        Finnish = "fi_FI" /** fi - Finnish */,
        French = "fr_FR" /** fr - French */,
        Gujarati = "gu_IN" /** gu - Gujarati */,
        Hebrew = "he_IL" /** he - Hebrew */,
        Croatian = "hr_HR" /** hr - Croatian */,
        Hungarian = "hu_HU" /** hu - Hungarian */,
        Myanmar = "my_MM" /** my - Myanmar/Burmese */,
        Armenian = "hy_AM" /** hy - Armenian */,
        Italian = "it_IT" /** it - Italian */,
        Kannada = "kn_IN" /** kn - Kannada */,
        Khmer = "km_KH" /** km - Khmer */,
        Korean = "ko_KR" /** ko - Korean */,
        Latvian = "lv_LV" /** lv - Latvian */,
        Lao = "lo_LA" /** lo - Lao */,
        Maltese = "mt_MT" /** mt - Maltese */,
        Malayalam = "ml_IN" /** ml - Malayalam */,
        Dutch = "nl_NL" /** nl - Dutch */,
        Norwegian = "no_NO" /** no - Norwegian */,
        Polish = "pl_PL" /** pl - Polish */,
        Portuguese = "pt_BR" /** pt_BR - Brazillian Portuguese */,
        Russian = "ru_RU" /** ru - Russian */,
        Slovenian = "sl_SL" /** sl - Slovenian */,
        Sinhalese = "si_LK" /** si - Sinhalese */,
        Swedish = "sv_SE" /** sv - Swedish */,
        Tamil = "ta_IN" /** ta - Tamil */,
        Thai = "th_TH" /** th - Thai  */,
        Telugu = "te_IN" /** te - Telugu  */,
        Turkish = "tr_TR" /** tr - Turkish */,
        Vietnamese = "vi_VN" /** vi - Vietnamese */,
        Mandarin = "zh_Latn_pinyin" /** zh-Latn-pinyin - Mandarin Chinese */,
        SimplifiedChinese = "zh_Hans" /** zh-Hans - SimplifiedChinese */,
        TraditionalChinese = "zh_Hant" /** zh-Hant - TraditionalChinese */,
        Belarusian = "be_BY" /** be - Belarusian */,
        Filipino = "fil_PH" /** fil - Filipino */,
        Indonesian = "id_ID" /** id- Indonesian */,
        Hindi = "hi_IN" /** hi - Hindi */,
        IPA = "IPA" /** IPA International Phonetic Alphabet */,
        Kazakh = "kk_KZ" /** kk - Kazakh */,
        Macedonian = "mk_MK" /** mk - Macedonian */,
        Malay = "ms_MY" /** ms - Malaysia */,
        Persian = "fa_IR" /** fa - Persian */,
        Romanian = "ro_RO" /** ro - Romanian */,
        Serbian = "sr_RS" /** sr - Serbian */,
        Slovak = "sk_SK" /** sk - Slovak */,
        Ukrainian = "uk_UA" /** uk - Ukrainian */,
    }

    export const RegionToLocale: Map<Language, Locale> = new Map<Language, Locale>([
        [Language.Default, Locale.Default /** This contains "en" and Languages other than below mentioned Languages. */],
        [Language.Japanese, Locale.Japanese /** ja - Japanese */],
        [Language.Devanagari, Locale.Hindi /** Deva / Hindi - Devanagari */],
        [Language.Gurmukhi, Locale.Gurmukhi /** Guru - Gurmukhi */],
        [Language.HongKongCh, Locale.HongKongCh /** HK - HongKongCh */],
        [Language.Arabic, Locale.Arabic /** ar - Arabic */],
        [Language.Bengali, Locale.Bengali /** bn - Bengali */],
        [Language.Catalan, Locale.Catalan /** ca - Catalan */],
        [Language.Cherokee, Locale.Cherokee /** chr - Cherokee */],
        [Language.Czech, Locale.Czech /** cs - Czech */],
        [Language.Danish, Locale.Danish /** da - Danish */],
        [Language.German, Locale.German /** de - German */],
        [Language.Greek, Locale.Greek /** el - Modern Greek */],
        [Language.Spanish, Locale.Spanish /** es - Spanish */],
        [Language.Emoji, Locale.Emoji /** emoji - Emoji */],
        [Language.Finnish, Locale.Finnish /** fi - Finnish */],
        [Language.French, Locale.French /** fr - French */],
        [Language.Gujarati, Locale.Gujarati /** gu - Gujarati */],
        [Language.Hebrew, Locale.Hebrew /** he - Hebrew */],
        [Language.Croatian, Locale.Croatian /** hr - Croatian */],
        [Language.Hungarian, Locale.Hungarian /** hu - Hungarian */],
        [Language.Norwegian, Locale.Norwegian /** nb - Norwegian */],
        [Language.Latvian, Locale.Latvian /** lv - Latvian */],
        [Language.Armenian, Locale.Armenian /** hy - Armenian */],
        [Language.Italian, Locale.Italian /** it - Italian */],
        [Language.Kannada, Locale.Kannada /** kn - Kannada */],
        [Language.Korean, Locale.Korean /** ko - Korean */],
        [Language.Maltese, Locale.Maltese /** mt - Maltese */],
        [Language.Malayalam, Locale.Malayalam /** ml - Malayalam */],
        [Language.Dutch, Locale.Dutch /** nl - Dutch */],
        [Language.Polish, Locale.Polish /** pl - Polish */],
        [Language.Portuguese, Locale.Portuguese /** pt - Portuguese */],
        [Language.Russian, Locale.Russian /** ru - Russian */],
        [Language.Slovenian, Locale.Slovenian /** sl - Slovenian */],
        [Language.Swedish, Locale.Swedish /** sv - Swedish */],
        [Language.Tamil, Locale.Tamil /** ta - Tamil */],
        [Language.Thai, Locale.Thai /** th - Thai  */],
        [Language.Telugu, Locale.Telugu /** te - Telugu  */],
        [Language.Turkish, Locale.Turkish /** tr - Turkish */],
        [Language.Vietnamese, Locale.Vietnamese /** vi - Vietnamese */],
        [Language.Mandarin, Locale.Mandarin /** zh-Latn-pinyin - Mandarin Chinese*/],
        [Language.SimplifiedChinese, Locale.SimplifiedChinese /** zh-Hans - SimplifiedChinese */],
        [Language.TraditionalChinese, Locale.TraditionalChinese /** zh-Hant - TraditionalChinese */],
        [Language.Belarusian, Locale.Belarusian /** Belarusian */],
        [Language.Filipino, Locale.Filipino /** Filipino */],
        [Language.Indonesian, Locale.Indonesian /** Indonesian */],
        [Language.Hindi, Locale.Hindi /** IPA */],
        [Language.IPA, Locale.IPA /** IPA */],
        [Language.Kazakh, Locale.Kazakh /** Kazakh */],
        [Language.Macedonian, Locale.Macedonian /** Macedonian  */],
        [Language.Malay, Locale.Malay /** Malaysian  */],
        [Language.Persian, Locale.Persian /** Persian */],
        [Language.Romanian, Locale.Romanian /** Romanian */],
        [Language.Serbian, Locale.Serbian /** Serbian */],
        [Language.Slovak, Locale.Slovak /** Slovak */],
        [Language.Ukrainian, Locale.Ukrainian /** Ukrainian */],
    ]);

    /**
     * A type that is defined to uniquely identify and find a font using either its postsciptName or its fontId (same as typekitId attribute of TQFont).
     * In most of the cases only one of the postscriptName or fontId would be be given, other can be supplied empty.
     * In case both of the values are provided as input, fontId would be given preference.
     * unicodes are optional to be supplied
     */
    export type TQFontIdentifer =
        | {
              postscriptName: string;
              fontId?: string;
              unicodes?: number[] | string;
          }
        | {
              postscriptName?: string;
              fontId: string;
              unicodes?: number[] | string;
          };

    export enum PrimaryScriptGroups {
        WESTERN = "WESTERN",
        JAPANESE = "JAPANESE",
        TRAD_CHINESE = "TRAD_CHINESE",
        SIMPLIFIED_CHINESE = "SIMPLIFIED_CHINESE",
        KOREAN = "KOREAN",
        ARABIC = "ARABIC",
        HEBREW = "HEBREW",
        INDIC = "INDIC",
        EMOJI = "EMOJI",
    }

    export enum InternalPrimaryScriptSubGroups {
        EMOJI_EMOJI = "EMOJI_EMOJI",

        WESTERN_DEFAULT = "WESTERN_DEFAULT",
        WESTERN_ARMENIAN = "WESTERN_ARMENIAN",
        WESTERN_BELARUSIAN = "WESTERN_BELARUSIAN",
        WESTERN_CATALAN = "WESTERN_CATALAN",
        WESTERN_CHEROKEE = "WESTERN_CHEROKEE",
        WESTERN_CROATIAN = "WESTERN_CROATIAN",
        WESTERN_CZECH = "WESTERN_CZECH",
        WESTERN_DANISH = "WESTERN_DANISH",
        WESTERN_DUTCH = "WESTERN_DUTCH",
        WESTERN_FILIPINO = "WESTERN_FILIPINO",
        WESTERN_FINNISH = "WESTERN_FINNISH",
        WESTERN_FRENCH = "WESTERN_FRENCH",
        WESTERN_GERMAN = "WESTERN_GERMAN",
        WESTERN_GREEK = "WESTERN_GREEK",
        WESTERN_HUNGARIAN = "WESTERN_HUNGARIAN",
        WESTERN_INDONESIAN = "WESTERN_INDONESIAN",
        WESTERN_ITALIAN = "WESTERN_ITALIAN",
        WESTERN_KAZAKH = "WESTERN_KAZAKH",
        WESTERN_LATVIAN = "WESTERN_LATVIAN",
        WESTERN_MACEDONIAN = "WESTERN_MACEDONIAN",
        WESTERN_MALTESE = "WESTERN_MALTESE",
        WESTERN_MANDARIN = "WESTERN_MANDARIN",
        WESTERN_NORWEGIAN = "WESTERN_NORWEGIAN",
        WESTERN_POLISH = "WESTERN_POLISH",
        WESTERN_PORTUGUESE = "WESTERN_PORTUGUESE",
        WESTERN_ROMANIAN = "WESTERN_ROMANIAN",
        WESTERN_RUSSIAN = "WESTERN_RUSSIAN",
        WESTERN_SERBIAN = "WESTERN_SERBIAN",
        WESTERN_SLOVAK = "WESTERN_SLOVAK",
        WESTERN_SLOVENIAN = "WESTERN_SLOVENIAN",
        WESTERN_SPANISH = "WESTERN_SPANISH",
        WESTERN_SWEDISH = "WESTERN_SWEDISH",
        WESTERN_TURKISH = "WESTERN_TURKISH",
        WESTERN_UKRAINIAN = "WESTERN_UKRAINIAN",
        WESTERN_VIETNAMESE = "WESTERN_VIETNAMESE",

        JAPANESE_JAPANESE = "JAPANESE_JAPANESE",

        SIMPLIFIED_CHINESE_SIMPLIFIEDCHINESE = "SIMPLIFIED_CHINESE_SIMPLIFIEDCHINESE",

        TRAD_CHINESE_HONGKONGCH = "TRAD_CHINESE_HONGKONGCH",
        TRAD_CHINESE_TRADITIONALCHINESE = "TRAD_CHINESE_TRADITIONALCHINESE",

        KOREAN_KOREAN = "KOREAN_KOREAN",

        ARABIC_ARABIC = "ARABIC_ARABIC",
        ARABIC_PERSIAN = "ARABIC_PERSIAN",

        HEBREW_HEBREW = "HEBREW_HEBREW",

        INDIC_DEVANAGARI = "INDIC_DEVANAGARI",
        INDIC_HINDI = "INDIC_HINDI",
        INDIC_BENGALI = "INDIC_BENGALI",
        INDIC_GUJARATI = "INDIC_GUJARATI",
        INDIC_GURMUKHI = "INDIC_GURMUKHI",
        INDIC_KANNADA = "INDIC_KANNADA",
        INDIC_MALAYALAM = "INDIC_MALAYALAM",
        INDIC_TAMIL = "INDIC_TAMIL",
        INDIC_TELUGU = "INDIC_TELUGU",
        INDIC_THAI = "INDIC_THAI",
        INDIC_MYANMAR = "INDIC_MYANMAR",
        INDIC_KHMER = "INDIC_KHMER",
        INDIC_SINHALESE = "INDIC_SINHALESE",
        INDIC_LAO = "INDIC_LAO",
        INDIC_MALAY = "INDIC_MALAY",
    }

    /** Strategy from wjhere to FetchFont */
    export enum FontFetchStrategy {
        ALL = 0, // will  fetch those fonts is present in the system or in Adobe server, prefering system fonts over Adobe.
        SYSTEM, // will only fetch those fonts is present in the system
        ADOBE, // will only fetch those font is present in the TypeKit server
    }

    export class Error {
        errCode: ErrorCode = ErrorCode.NONE;
        errMsg = "No Error";
        isFatal = false;

        constructor(errorCode: ErrorCode = ErrorCode.NONE, errorMessage = "No Error", fatal = false) {
            this.errCode = errorCode;
            this.errMsg = errorMessage;
            if (this.errCode != ErrorCode.NONE && this.errMsg == "No Error") {
                this.errMsg = "Error occurred";
            }
            this.isFatal = fatal;
        }
    }

    /** Specifies which kind of font/fonts were found */
    export enum FontType {
        DEFAULT = "DEFAULT",
        SYSTEM = "SYSTEM",
        LOCAL = "LOCAL",
        ACTIVATED = "ACTIVATED",
        BYOF = "BYOF",
        DEMO = "DEMO",
        APPLICATION = "APPLICATION",
        // RECOMMENDED = "RECOMMENDED",
        // TRENDING = "TRENDING",
        // NEWEST = "NEWEST",
        // HIDDEN_GEMS = "HIDDEN_GEMS",
        // STAFF_CHOICE = "STAFF_CHOICE",
    }

    /** Specifies which kind of custom catalog is required */
    export enum ApplicationFontsType {
        VARIABLE = "variable_fonts",
        PHOTOSHOP = "ps_fonts",
    }

    /** Specifies where font was found */
    export enum Entitlement {
        FULL = "full",
        TRIAL = "trial",
        ANONYMOUS = "anonymous",
    }

    /** Font update operations */
    export enum FontUpdateOperationType {
        ADDED = "ADDED",
        REMOVED = "REMOVED",
    }

    export type InternalFontUpdateNotification = {
        fontInfo: string; // can be psName of variation id
        updateType: FontUpdateOperationType; // Whether font was added or removed
        isVariationId: boolean; // To denote if it's a variation id or psname
        contextData: any;
    };

    export enum FontUpdateNotificationType {
        DEFAULT = 0,
        SELECTIONS = 1,
        BYOF = 2,
    }

    export class FontUpdateNotification {
        private _font: TQFont;
        private _operationType: FontUpdateOperationType; // Whether font was added or removed
        private _notificationType: FontUpdateNotificationType; // What kind of a notification is this?

        public get font(): TQFont {
            return this._font;
        }
        public set font(value: TQFont) {
            this._font = value;
        }

        public get operationType(): FontUpdateOperationType {
            return this._operationType;
        }
        public set operationType(value: FontUpdateOperationType) {
            this._operationType = value;
        }

        public get notificationType(): FontUpdateNotificationType {
            return this._notificationType;
        }
        public set notificationType(value: FontUpdateNotificationType) {
            this._notificationType = value;
        }

        constructor(font: TQFont, operationType: FontUpdateOperationType, notificationType: FontUpdateNotificationType = FontUpdateNotificationType.DEFAULT) {
            this._font = font;
            this._operationType = operationType;
            this._notificationType = notificationType;
        }
    }

    export class ObtainedTokenParams {
        private _fasterEndPointToken = "";
        private _token = "";
        private _expiry = 0;
        private _catalog = "";
        private _genericdownloadUrl = "";
        private _entitlement: Entitlement = Entitlement.TRIAL;
        private _mapOfCustomCatalogParams = new Map<string, ObtainedTokenParams>();

        public get fasterEndPointToken() {
            return this._fasterEndPointToken;
        }
        public set fasterEndPointToken(value) {
            this._fasterEndPointToken = value;
        }

        public get genericdownloadUrl() {
            return this._genericdownloadUrl;
        }
        public set genericdownloadUrl(value) {
            this._genericdownloadUrl = value;
        }

        public get mapOfCustomCatalogParams() {
            return this._mapOfCustomCatalogParams;
        }
        public set mapOfCustomCatalogParams(value) {
            this._mapOfCustomCatalogParams = value;
        }

        public get token(): string {
            return this._token;
        }
        public set token(value: string) {
            this._token = value;
        }

        public get catalog(): string {
            return this._catalog;
        }
        public set catalog(value: string) {
            this._catalog = value;
        }

        public get expiry(): number {
            return this._expiry;
        }
        public set expiry(value: number) {
            this._expiry = value;
        }

        public get entitlement(): Entitlement {
            return this._entitlement;
        }
        public set entitlement(value: Entitlement) {
            this._entitlement = value;
        }
    }

    export class AuthParams {
        /** Params to be used in use fonts API at https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=fonts&title=Font+Uses+API Currently optional*/
        private _appId: string;
        private _appVersion = "";
        private _userId = ""; // If empty, they will be picked from adobeIMS object
        private _accessToken = ""; // If empty, they will be picked from adobeIMS object

        /** Client Application identifier for receiving notifications from ANS.
         * TQWeb would only check for BYOF upload workflow notifications. This would happen via a short poll over Query notifications API.
         * Please do the required onboarding before initializing this parameter.
         * Documentation at https://wiki.corp.adobe.com/display/ANS/ANS+API+Reference#ANSAPIReference-QueryNotificationsAPI
         */
        private _ansAppIdentifier = "";

        private _isStaging = false;
        private _proxyUsername = "";
        private _proxyPassword = "";

        constructor(appId: string, userId: string, accessToken: string) {
            this._appId = appId;
            this._userId = userId;
            this._accessToken = accessToken;
        }

        public get appId(): string {
            return this._appId;
        }
        public set appId(value: string) {
            this._appId = value;
        }

        public get appVersion(): string {
            return this._appVersion;
        }
        public set appVersion(value: string) {
            this._appVersion = value;
        }

        public get userId(): string {
            return this._userId;
        }
        public set userId(value: string) {
            this._userId = value;
        }

        public get accessToken(): string {
            return this._accessToken;
        }
        public set accessToken(value: string) {
            this._accessToken = value;
        }

        public get isStaging(): boolean {
            return this._isStaging;
        }
        public set isStaging(value: boolean) {
            this._isStaging = value;
        }

        public get proxyUsername(): string {
            return this._proxyUsername;
        }
        public set proxyUsername(value: string) {
            this._proxyUsername = value;
        }

        public get proxyPassword(): string {
            return this._proxyPassword;
        }
        public set proxyPassword(value: string) {
            this._proxyPassword = value;
        }

        public get ansAppIdentifier() {
            return this._ansAppIdentifier;
        }
        public set ansAppIdentifier(value) {
            this._ansAppIdentifier = value;
        }
    }

    export enum OperationMode {
        DATA = 0, // will schedule download for a data if data is not present in TC cache
        CHECK, // will populate provided attributes but won't download data if absent in TCCache // TODO
        FORCE_REFRESH, // will force fetch the data and override the data present on TCCache, if any // TODO
    }

    export enum FontUsageOperation {
        OPEN = "open",
        APPLY = "apply",
        // NOT_IN_USE = "not_in_use"
    }

    export enum RecentsUpdatePreference {
        ONLY_APPLY,
        ONLY_OPEN,
        BOTH_OPEN_AND_APPLY,
    }

    export class ProgressInfo {
        private _downloadBytes = 0;
        private _totalBytes = 0;

        public get totalBytes(): number {
            return this._totalBytes;
        }
        public set totalBytes(value: number) {
            this._totalBytes = value;
        }

        public get downloadBytes(): number {
            return this._downloadBytes;
        }
        public set downloadBytes(value: number) {
            this._downloadBytes = value;
        }
    }
}

export default class TQFont {
    /** @brief FontInfo for font available to clients CB's */
    private _type: tqtypes.FontType = tqtypes.FontType.DEFAULT;
    public _subtype: tqtypes.ApplicationFontsType | undefined = undefined;
    private _postscriptName: string;
    private _osCompatiblePostscriptName: string;
    private _fontId: string;
    private _isActiveForUser = false;
    private _fontBuffer: Uint8Array = new Uint8Array(0);
    private _expiryTime = 0; // Absolute time in epoch when the font expires. If 0, non-cacheable.
    private _serverTimestamp = 0; // Absolute time in epoch when the font was added to service. If 0, unknown
    private _family: TQFontFamily | undefined;
    private _filePath = "";
    private _fontName = "";
    private _styleName = "";
    private _familyName = "";
    private _sortIndex = 0;
    private _script: tqtypes.Language = tqtypes.Language.Default;
    private _supportedLanguages: Set<tqtypes.Language> = new Set<tqtypes.Language>([tqtypes.Language.Default]);
    private _characterSet = new Set<number>();
    private _localizedNames = new Map<tqtypes.Language, string>();
    private static _omittedKeys = ["_family", "_fontBuffer", "_characterSet"];

    /**
     * By default, only read_only access is given for a premium font. Please make sure TQWeb is started before this attribute is checked
     */
    private _accessLevel = tqtypes.AccessLevel.READ_ONLY;
    private _isPremiumFont = false; // By default we assume it is not premium and thus entitled to be used.
    private _isBrowsable = true; // By default we assume every font is browsable

    // ======= variable font attributes =======
    private _isVariableFont = false;
    private _variableFamilyPrefix = "";
    private _varAxisValues = new Map<string, number>();

    constructor(type: tqtypes.FontType = tqtypes.FontType.DEFAULT, psName = "", tkId = "") {
        this._postscriptName = psName;
        this._osCompatiblePostscriptName = psName;
        this._fontId = tkId;
        this._type = type;
    }

    public clone(excludeKeys: any[] = []): any {
        const cloneObj = new TQFont() as any;
        TQUtils.copyAttributes(excludeKeys, this, cloneObj);
        return cloneObj;
    }

    public toJSON(): any {
        const cloneObj: any = {};
        TQUtils.copyAttributes(TQFont._omittedKeys, this, cloneObj);
        return JSON.stringify(cloneObj);
    }

    public static fromJSON(jsonString: string): TQFont {
        const font = new TQFont() as any;
        try {
            const obj = JSON.parse(jsonString);
            TQUtils.copyAttributes(TQFont._omittedKeys, obj, font);
        } catch (e) {
            // ...
        }
        return font;
    }

    // ======= Getters & setters =======
    public get varAxisValues() {
        return this._varAxisValues;
    }
    public set varAxisValues(value) {
        this._varAxisValues = value;
    }

    public get variableFamilyPrefix() {
        return this._variableFamilyPrefix;
    }
    public set variableFamilyPrefix(value) {
        this._variableFamilyPrefix = value;
    }

    public get isVariableFont() {
        return this._isVariableFont;
    }
    public set isVariableFont(value) {
        this._isVariableFont = value;
    }

    public get supportedLanguages(): Set<tqtypes.Language> {
        return this._supportedLanguages;
    }

    public set supportedLanguages(langs: Set<tqtypes.Language>) {
        this._supportedLanguages = langs;
    }

    public addSupportedLanguage(value: tqtypes.Language) {
        this._supportedLanguages.add(value);
    }

    public get isBrowsable() {
        return this._isBrowsable;
    }
    public set isBrowsable(value) {
        this._isBrowsable = value;
    }

    public get characterSet() {
        return this._characterSet;
    }
    public set characterSet(value) {
        this._characterSet = value;
    }

    public get sortIndex(): number {
        return this._sortIndex;
    }
    public set sortIndex(value: number) {
        this._sortIndex = value;
    }

    public get accessLevel() {
        return this._accessLevel;
    }

    public set accessLevel(value) {
        this._accessLevel = value;
    }

    public set setPremiumFont(value: boolean) {
        this._isPremiumFont = value;
    }

    public get isPremiumFont(): boolean {
        return this._isPremiumFont;
    }

    /** Only should be called from within TQWeb, not from external clients */
    public set isPremiumFont(value: boolean) {
        this._isPremiumFont = value;
    }

    public get familyName(): string {
        return this._familyName;
    }
    public set familyName(value: string) {
        this._familyName = value;
    }

    public get localizedNames() {
        if (this._localizedNames.size == 0) {
            this._localizedNames.set(TQUtils.getAppropriateRegion(), this._fontName);
        }
        return this._localizedNames;
    }
    public set localizedNames(value) {
        this._localizedNames = value;
    }

    public get styleName(): string {
        return this._styleName;
    }
    public set styleName(value: string) {
        this._styleName = value;
    }

    public get filePath(): string {
        return this._filePath;
    }
    public set filePath(value: string) {
        this._filePath = value;
    }

    public get type(): tqtypes.FontType {
        return this._type;
    }
    public set type(value: tqtypes.FontType) {
        this._type = value;
    }

    public get fontBuffer(): Uint8Array {
        return this._fontBuffer;
    }
    public set fontBuffer(value: Uint8Array) {
        this._fontBuffer = value;
    }

    public get postscriptName(): string {
        return this._postscriptName;
    }
    public set postscriptName(value: string) {
        this._postscriptName = value;
    }

    public get osCompatiblePostscriptName(): string {
        return this._osCompatiblePostscriptName;
    }
    public set osCompatiblePostscriptName(value: string) {
        this._osCompatiblePostscriptName = value;
    }

    public get script(): tqtypes.Language {
        return this._script;
    }
    public set script(value: tqtypes.Language) {
        this._script = value;
    }

    /**
     * Earlier this was known as typekitId. fontId is the umbrella term for both tkId and BYOF font id.
     */
    public get fontId(): string {
        return this._fontId;
    }

    public set fontId(value: string) {
        this._fontId = value;
    }

    public get isActiveForUser(): boolean {
        return this._isActiveForUser;
    }
    public set isActiveForUser(value: boolean) {
        this._isActiveForUser = value;
    }

    public get expiryTime(): number {
        return this._expiryTime;
    }
    public set expiryTime(value: number) {
        this._expiryTime = value;
    }

    public get serverTimestamp() {
        return this._serverTimestamp;
    }
    public set serverTimestamp(value) {
        this._serverTimestamp = value;
    }

    public get family(): TQFontFamily | undefined {
        return this._family;
    }
    public set family(value: TQFontFamily | undefined) {
        this._family = value;
    }

    public get fontName(): string {
        return this._fontName;
    }
    public set fontName(value: string) {
        this._fontName = value;
    }

    /**
     * This function would generate a custom OTVar instance postscript name and return it.
     * Prerequisite: Axis ordering data should be present hence fontBuffer of the TQFont object must be present
     * @param axisValues
     */
    public generateCustomOTVarInstance(axisValues: Map<string, number>): string | undefined {
        if (this._fontBuffer.length) {
            if (this.isVariableFont && axisValues.size) {
                for (const scheduler of TQTaskScheduler._schedulersList) {
                    for (const [, font] of scheduler.cachedFonts()) {
                        if (font.isVariableFont && font.variableFamilyPrefix == this.variableFamilyPrefix) {
                            if (TQUtils.equalMaps(font.varAxisValues, axisValues)) {
                                return font.postscriptName;
                            }
                        }
                    }
                }
                return TQUtils.generateCustomOTVarName(this, axisValues);
            } else {
                return this._postscriptName;
            }
        }
        return undefined;
    }

    public isCurrentFontPreferredOver(otherfont: TQFont): boolean {
        const fontPreferenceResult = (passedTypes: tqtypes.FontType[]) => {
            for (const passedType of passedTypes) {
                if (this.type == passedType) {
                    return true;
                }
                if (otherfont.type == passedType) {
                    return false;
                }
            }
            return undefined;
        };

        const preferenceTestResult = fontPreferenceResult([
            tqtypes.FontType.BYOF,
            tqtypes.FontType.ACTIVATED,
            tqtypes.FontType.DEFAULT,
            tqtypes.FontType.APPLICATION,
            tqtypes.FontType.LOCAL,
            tqtypes.FontType.SYSTEM,
        ]);

        return preferenceTestResult == undefined ? true : preferenceTestResult;
    }
}

export class TQFontFamily {
    private _familyName: string;
    private _displayFont: TQFont = new TQFont();
    private _fontMap: Map<string, TQFont> = new Map();
    private _isPremium = true;

    public get familyName() {
        return this._familyName;
    }
    public set familyName(value) {
        this._familyName = value;
    }

    public get script() {
        return this._displayFont.script;
    }

    public get isPremium() {
        return this._isPremium;
    }

    public get displayFont(): TQFont {
        return this._displayFont;
    }
    public set displayFont(value: TQFont) {
        this._displayFont = value;
    }

    public addFont(psName: string, font: TQFont) {
        const fontIfExists = this._fontMap.get(psName);
        if (fontIfExists && fontIfExists.isCurrentFontPreferredOver(font)) {
            // do nothing
        } else {
            this._fontMap.set(psName, font);
        }
        this._isPremium = this._isPremium && font.isPremiumFont; // Even If one font is free, family is not premium.
    }

    public removeFont(psName: string) {
        this._fontMap.delete(psName);
    }

    constructor(famName = "") {
        this._familyName = famName;
    }

    public getEntries() {
        const fonts: TQFont[] = [];
        this._fontMap.forEach((value, key) => {
            fonts.push(value);
        });
        const isVariableFontFamily = this._displayFont.isVariableFont;

        const differentiator = (a: any, b: any, axisTag: string) => {
            const aAxisTag = a.varAxisValues.get(axisTag);
            const bAxisTag = b.varAxisValues.get(axisTag);
            if (aAxisTag && bAxisTag && aAxisTag != bAxisTag) {
                return aAxisTag - bAxisTag;
            }
        };

        fonts.sort((a, b) => {
            if (isVariableFontFamily) {
                if (a.sortIndex == undefined) {
                    a.sortIndex = 0;
                }
                if (b.sortIndex == undefined) {
                    b.sortIndex = 0;
                }
                return a.sortIndex - b.sortIndex;
                // let result: any = undefined;
                // const orderings = ["wdth", "wght", "slnt", "ital", "opsz"];
                // for (const ordering of orderings) {
                //     result = differentiator(a, b, ordering);
                //     if (result) {
                //         return result;
                //     }
                // }
                // return String(a.styleName).localeCompare(b.styleName);
            } else {
                return a.sortIndex - b.sortIndex;
            }
        });
        return fonts;
    }

    public getRawUnorderedMap() {
        return this._fontMap;
    }
}

export class TQFontAggregation {
    private _familyMap: Map<string, TQFontFamily> = new Map();

    public addFamily(famName: string, family: TQFontFamily) {
        this._familyMap.set(famName, family);
    }

    public removeFamily(famName: string) {
        this._familyMap.delete(famName);
    }

    public getFamilyFromName(famName: string): TQFontFamily | undefined {
        return this._familyMap.get(famName);
    }

    public getRawMap() {
        return this._familyMap;
    }

    public addFonts(fonts: TQFont[]) {
        const updates: tqtypes.FontUpdateNotification[] = [];
        for (const font of fonts) {
            updates.push(new tqtypes.FontUpdateNotification(font, tqtypes.FontUpdateOperationType.ADDED));
        }
        this.handleUpdates(updates);
    }

    public handleUpdates(updates: tqtypes.FontUpdateNotification[]) {
        for (const entry of updates) {
            const font = entry.font;
            if (font.familyName != "") {
                let family = this._familyMap.get(font.familyName);
                if (entry.operationType == tqtypes.FontUpdateOperationType.ADDED) {
                    if (family) {
                        // ...
                        family.addFont(font.postscriptName, font);
                    } else {
                        family = new TQFontFamily(font.familyName);
                        this.addFamily(font.familyName, family);
                        family.addFont(font.postscriptName, font);
                        family.displayFont = font;
                    }
                } else {
                    if (family) {
                        // ...
                        family.removeFont(font.postscriptName);
                        // Check if we should reassign display font
                        if (family.displayFont.postscriptName == font.postscriptName && family.getRawUnorderedMap().size) {
                            family.displayFont = family.getEntries()[0];
                        } else if (family.getRawUnorderedMap().size == 0) {
                            // remove the family as well
                            this._familyMap.delete(font.familyName);
                        }
                    } else {
                        // Do nothing, as its already absent
                    }
                }
            }
        }
        for (const scheduler of TQTaskScheduler._schedulersList) {
            if (scheduler.initParams.fontsUpdateCallback) {
                TQTaskScheduler.updateCachedSelectionsAndByof(updates, [scheduler]);
            }
        }
    }

    public getEntries(
        sortOrder: tqtypes.SortOrderPreference = tqtypes.SortOrderPreference.DEFAULT,
        userEntitlement: tqtypes.Entitlement = tqtypes.Entitlement.FULL,
        langtoBePreferred: tqtypes.Language | undefined = undefined
    ) {
        switch (sortOrder) {
            case tqtypes.SortOrderPreference.LANGUAGE: {
                const subGroupMap = new Map<tqtypes.InternalPrimaryScriptSubGroups, TQFontAggregation>();
                let targetRegion = TQUtils.getAppropriateRegion();
                if (langtoBePreferred) {
                    targetRegion = langtoBePreferred;
                }
                const scriptGroup: tqtypes.PrimaryScriptGroups = TQUtils.mapLangToPrimaryScriptGroup(targetRegion);
                //subGroupMap.set(tqtypes.InternalPrimaryScriptSubGroups.EMOJI_EMOJI, new TQFontAggregation());
                for (const item in tqtypes.InternalPrimaryScriptSubGroups) {
                    if (item.toString().indexOf(scriptGroup as string) != -1) {
                        subGroupMap.set(item as tqtypes.InternalPrimaryScriptSubGroups, new TQFontAggregation());
                    }
                }

                for (const item in tqtypes.InternalPrimaryScriptSubGroups) {
                    if (subGroupMap.has(item as tqtypes.InternalPrimaryScriptSubGroups) == false) {
                        subGroupMap.set(item as tqtypes.InternalPrimaryScriptSubGroups, new TQFontAggregation());
                    }
                }

                for (const [familyName, family] of this._familyMap) {
                    const scriptSubGroup: tqtypes.InternalPrimaryScriptSubGroups = TQUtils.mapLangToInternalPrimaryScriptSubgroup(family.script);
                    const aggregation = subGroupMap.get(scriptSubGroup);
                    if (aggregation) {
                        aggregation.addFamily(familyName, family);
                    }
                }

                for (const [script, aggregation] of subGroupMap) {
                    aggregation._familyMap = new Map(
                        [...aggregation._familyMap].sort((a, b) => {
                            return String(a[0]).localeCompare(b[0]);
                        })
                    );
                }

                const familyMap = new Map<string, TQFontFamily>();
                for (const [script, aggregation] of subGroupMap) {
                    for (const [familyName, family] of aggregation._familyMap) {
                        familyMap.set(familyName, family);
                    }
                }
                return familyMap;
            }

            case tqtypes.SortOrderPreference.DEFAULT: {
                return new Map(
                    [...this._familyMap].sort((a, b) => {
                        return String(a[0]).localeCompare(b[0]);
                    })
                );
            }
            case tqtypes.SortOrderPreference.ENTITLEMENT: {
                return new Map(
                    [...this._familyMap].sort((a, b) => {
                        if (userEntitlement == tqtypes.Entitlement.TRIAL) {
                            if (a[1].isPremium == false && b[1].isPremium == true) {
                                return -1;
                            }

                            if (a[1].isPremium == true && b[1].isPremium == false) {
                                return 1;
                            }
                        }
                        return String(a[0]).localeCompare(b[0]);
                    })
                );
            }

            case tqtypes.SortOrderPreference.RECENTLY_UPDATED: {
                const familyNameVsExpiryMap = new Map<string, number>();
                this._familyMap.forEach(entry => {
                    let recentlyUpdatedEntryForFamily = 0;
                    for (const font of entry.getEntries()) {
                        if (font.serverTimestamp > recentlyUpdatedEntryForFamily) {
                            recentlyUpdatedEntryForFamily = font.serverTimestamp;
                        }
                    }
                    familyNameVsExpiryMap.set(entry.familyName, recentlyUpdatedEntryForFamily);
                });

                return new Map(
                    [...this._familyMap].sort((a, b) => {
                        const recentTimeFamilyA = familyNameVsExpiryMap.get(a[0]);
                        const recentTimeFamilyB = familyNameVsExpiryMap.get(b[0]);
                        if (recentTimeFamilyA && recentTimeFamilyB) {
                            return recentTimeFamilyB - recentTimeFamilyA;
                        } else if (recentTimeFamilyA) {
                            return -1;
                        } else if (recentTimeFamilyB) {
                            return 1;
                        }
                        return String(a[0]).localeCompare(b[0]);
                    })
                );
            }
        }
    }

    public merge(otherAggregation: TQFontAggregation) {
        // let mergedFamilyMap:Map<string, TQFontFamily> = new Map();
        // create final aggregation a copy of current aggregation
        // this._familyMap.forEach((family, name) => {
        //     mergedFamilyMap.set(name, family);
        // });
        otherAggregation._familyMap.forEach((family, name) => {
            const familyIfExists = this._familyMap.get(name);
            if (familyIfExists != undefined) {
                // Exists already in our map, needs deep merging
                // Deep merging logic
                family.getEntries().forEach(font => {
                    const fontIfExists = familyIfExists.getRawUnorderedMap().get(font.postscriptName);
                    if (fontIfExists == undefined) {
                        // Add it to our family
                        familyIfExists.addFont(font.postscriptName, font);
                    } else {
                        // font also exists in our family, pick the most preferred font now
                        if (font.isCurrentFontPreferredOver(fontIfExists)) {
                            familyIfExists.addFont(font.postscriptName, font);
                        } else {
                            // No need to do anything,
                            // fontIfExists is already in our familyIfExists
                        }
                    }
                });

                if (family.displayFont.type == tqtypes.FontType.DEFAULT && familyIfExists.displayFont.type == tqtypes.FontType.ACTIVATED) {
                    // okay, take this
                    familyIfExists.displayFont = family.displayFont;
                }
            } else {
                // Not present in our map, copy full family
                this._familyMap.set(name, family);
            }
        });
    }
}

export namespace tqtypes {
    export enum TaskType {
        TOKEN_FETCH = 0,
        TOKEN_FETCH_INTERNAL,
        FONT_SEARCH, // internal font search used for regular findFont
        FONT_SEARCH_INTERNAL, // internal font search used for regular findFont
        FONT_DOWNLOAD,
        TAG_CATALOG_URL,
        USAGE_REPORT,
        FILE_DOWNLOAD, // generic
        FILE_UPLOAD, // generic
        FONT_CATALOG_URL,
        ACTIVATED_FONTS_POLL,
        ACTIVATED_FONTS,
        ACTIVATED_FONTS_COUNT,
        FETCH_FAMILY,
        SEARCH_FAMILY,
        FETCH_LANGS,
        CATALOG_DOWNLOAD,
        CUSTOM_CATALOG_URL_VARIABLE,
        CUSTOM_CATALOG_URL_PHOTOSHOP,
        CUSTOM_CATALOG_DOWNLOAD,
        BYOF_COUNT,
        BYOF_LISTS,
        BYOF_UPLOAD_URL,
        BYOF_BLOCKLIST_CHECK,
        BYOF_FONTS_POLL,
        BYOF_ACCESS_LEVEL,
        BYOF_DELETE,
        RECOMMENDATION_URL,
        RECOMMENDED_SLUGS,
        NEWEST_SLUGS,
        TRENDING_SLUGS,
        ANS_SHORT_POLL,
        COLLAB_AUTH_API,
    }

    export type FontDownloadCallback = (fontInfo: TQFont, error: tqtypes.Error) => boolean;
    export type FontProgressCallback = (fontInfo: TQFont, progressInfo: tqtypes.ProgressInfo) => boolean;
    export type FontCollectionCompletionCallback = (fontType: FontType, collection: TQFontAggregation, error: tqtypes.Error) => boolean;

    export type InternalContext = {
        fontsList: string[];
        fontsIds: string[];
        fontInfo: TQFont;
        unicodesList: number[] | string;
        downloadCB: FontDownloadCallback;
    };

    export type UsageInternalContext = {
        fontsList: string[];
        documentId: string;
        operation: FontUsageOperation;
        autoActivate: boolean;
        installState: tqtypes.InstallState;
        readonly: boolean;
    };

    export interface GenericContext<T> {
        root: T;
        retryCount: number;
    }

    export type AggregationContext = {
        page: number;
        perPage: number;
        isRequested: boolean;
    };

    export type FetchFamilyContext = {
        familySlug: string;
    };

    export type UploadContext = {
        file: File;
    };

    export interface FontCollaborationInternalContext {
        fontInfo: TQFont;
        error: tqtypes.Error;
        downloadUrl: string | undefined;
    }

    export type CollaborationAuthInternalContext = {
        psNames: string[];
        fontIDs: string[];
        fontAccessToken: string;
        documentId: string;
        psNamesInfo: Map<string, TQFont>;
        fontIDsInfo: Map<string, TQFont>;
    };

    export class VariationTag {
        private _tagId = "";
        private _namespaceId = "";
        private _tagName = "";
        private _synonyms: Set<string> = new Set<string>();
        private _displayFont: TQFont = new TQFont();
        private _aggregation: TQFontAggregation = new TQFontAggregation();

        private _executorDisplayFontInternal: any = undefined;
        private _executorAggregatorInternal: any = undefined;

        public get executorDisplayFontInternal(): any {
            return this._executorDisplayFontInternal;
        }
        public set executorDisplayFontInternal(value: any) {
            this._executorDisplayFontInternal = value;
        }

        public get executorAggregatorInternal(): any {
            return this._executorAggregatorInternal;
        }
        public set executorAggregatorInternal(value: any) {
            this._executorAggregatorInternal = value;
        }

        public get tagId(): string {
            return this._tagId;
        }
        public set tagId(value: string) {
            this._tagId = value;
        }

        public get namespaceId(): string {
            return this._namespaceId;
        }
        public set namespaceId(value: string) {
            this._namespaceId = value;
        }

        public get tagName(): string {
            return this._tagName;
        }
        public set tagName(value: string) {
            this._tagName = value;
        }

        public get synonyms(): Set<string> {
            return this._synonyms;
        }
        public set synonyms(value: Set<string>) {
            this._synonyms = value;
        }

        public get displayFont(): Promise<TQFont> {
            return (async () => {
                try {
                    if (this._executorDisplayFontInternal != undefined) {
                        this._displayFont = await this._executorDisplayFontInternal();
                        this._executorDisplayFontInternal = undefined;
                    }
                } catch (e) {
                    // ..
                }
                return this._displayFont;
            })();
        }

        public get aggregation(): Promise<TQFontAggregation> {
            return (async () => {
                try {
                    if (this._executorAggregatorInternal != undefined) {
                        this._aggregation = await this._executorAggregatorInternal();
                        this._executorAggregatorInternal = undefined;
                    }
                } catch (e) {
                    // ..
                }
                return this._aggregation;
            })();
        }

        public setAggregation(value: TQFontAggregation) {
            this._aggregation = value;
        }

        public constructor(id: string, name: string) {
            this._tagId = id;
            this._tagName = name;
        }
    }

    export type VariationTagListCallback = (tags: Map<string, VariationTag>, error: tqtypes.Error) => boolean;

    /** callback to be called from application with the data that TQWeb provides and their aggregation which needs updates */
    export type FontUpdateNotificationHandler = (aggregation: TQFontAggregation) => boolean;
}

export enum TQLogLevel {
    NONE = 0, // Lowest level
    TRACE = 1,
    DEBUG = 2,
    INFO = 3,
    WARN = 4,
    ERROR = 5, // Highest level
}
